import { useState } from "react"
import { useParams } from "react-router"

import { type DnsRecordType, dnsRecordTypes } from "@forento/core/models/domain"
import { parseNumber } from "@forento/core/utilities/number"

import Dropdown from "~/components/Dropdown"
import InputField from "~/components/InputField"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import LoadingIndicator from "~/components/LoadingIndicator"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import routes from "~/utilities/routes"
import { dangerColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

const title = (
	<PageBreadcrumb
		title="Manage DNS"
		path={[
			{ title: "Settings", link: routes.settings.index() },
			{ title: "Custom Domain", link: routes.settings.domain.index() },
		]}
	/>
)

const ManageDnsPage = withAccessRequirement("customDomain", title, () => {
	const alert = useAlert()

	const domainPurchaseId = parseNumber(useParams().domainPurchaseId) ?? -1

	const [editingId, setEditingId] = useState<number>()
	const [editingType, setEditingType] = useState<DnsRecordType>("A")
	const [editingHost, setEditingHost] = useState("")
	const [editingValue, setEditingValue] = useState("")
	const [isSubmittingEdited, setSubmittingEdited] = useState(false)

	const [newType, setNewType] = useState<DnsRecordType>("A")
	const [newHost, setNewHost] = useState("")
	const [newValue, setNewValue] = useState("")
	const [isSubmittingNew, setSubmittingNew] = useState(false)

	const [deletingRecordId, setDeletingRecordId] = useState<number>()

	const { data: dnsRecords, error, refetch } = query.domain.listDnsRecords.useQuery(domainPurchaseId)

	async function handleUpdateRecord() {
		if (editingId === undefined || editingHost.trim().length === 0 || editingValue.trim().length === 0) return
		setSubmittingEdited(true)
		try {
			const { status } = await trpc.domain.updateDnsRecord.mutate({
				id: editingId,
				data: { type: editingType, host: editingHost, value: editingValue },
			})
			if (status === "conflict") {
				await alert.show(
					"Error",
					"This record conflicts with another record. Please change it or remove the conflicting one.",
				)
				setSubmittingEdited(false)
				return
			}
			if (status === "invalid") {
				await alert.show("Error", "This record is invalid. Make sure you have chosen the right record type.")
				setSubmittingEdited(false)
				return
			}
			await refetch()
			setEditingId(undefined)
		} catch (error) {
			console.error(error)
			setSubmittingEdited(false)
		}
		setSubmittingEdited(false)
	}

	async function handleAddRecord() {
		if (newHost.trim().length === 0 || newValue.trim().length === 0) return

		setSubmittingNew(true)
		try {
			const { status } = await trpc.domain.addDnsRecord.mutate({
				domainPurchaseId,
				record: { type: newType, host: newHost, value: newValue },
			})
			if (status === "conflict") {
				await alert.show(
					"Error",
					"This record conflicts with another record. Please change it or remove the conflicting one.",
				)
				setSubmittingNew(false)
				return
			}
			if (status === "invalid") {
				await alert.show("Error", "This record is invalid. Make sure you chose the right type.")
				setSubmittingNew(false)
				return
			}
			await refetch()
			setNewType("A")
			setNewHost("")
			setNewValue("")
		} catch (error) {
			console.error(error)
			setSubmittingNew(false)
		}
		setSubmittingNew(false)
	}

	async function handleDeleteRecord(id: number) {
		setDeletingRecordId(id)
		try {
			await trpc.domain.deleteDnsRecord.mutate(id)
			await refetch()
		} catch (error) {
			console.error(error)
			setDeletingRecordId(undefined)
		}
		setDeletingRecordId(undefined)
	}

	return (
		<Layout>
			{title}
			{error ? (
				<p>Failed to load DNS records.</p>
			) : !dnsRecords ? (
				<PartialLoadingPage />
			) : (
				<Table
					columns={["Type", "Host", "Value", { label: "Manage", flex: { basis: 130, grow: 0 } }]}
					data={[
						...dnsRecords.map(record =>
							record.isCustom && record.id === editingId
								? [
										<Dropdown
											items={dnsRecordTypes.map(x => ({ id: x, title: x }))}
											selectedItemId={editingType}
											onChange={type =>
												setEditingType(dnsRecordTypes.find(x => x === type) ?? "A")
											}
										/>,
										<InputField
											compact
											placeholder="@"
											value={editingHost}
											onChange={setEditingHost}
										/>,
										<InputField
											compact
											placeholder="192.168.1.1"
											value={editingValue}
											onChange={setEditingValue}
										/>,
										<div className="flex justify-end gap-2">
											<button
												type="button"
												onClick={() => setEditingId(undefined)}
												disabled={isSubmittingEdited}
												className="text-primary cursor-pointer text-sm font-semibold disabled:cursor-auto"
											>
												Cancel
											</button>
											<button
												type="button"
												onClick={handleUpdateRecord}
												disabled={isSubmittingEdited}
												className="text-primary flex cursor-pointer items-center gap-2 text-sm font-semibold disabled:cursor-auto"
											>
												{isSubmittingEdited && <LoadingIndicator size={12} />}
												Save
											</button>
										</div>,
									]
								: [
										record.type,
										record.host,
										record.value,
										record.isCustom && (
											<div className="flex justify-end gap-2">
												<button
													type="button"
													onClick={() => {
														setEditingId(record.id)
														setEditingType(record.type)
														setEditingHost(record.host)
														setEditingValue(record.value)
													}}
													className="text-primary cursor-pointer text-sm font-semibold"
												>
													Edit
												</button>
												<button
													type="button"
													onClick={() => handleDeleteRecord(record.id)}
													disabled={deletingRecordId !== undefined}
													className="text-danger flex cursor-pointer items-center gap-2 text-sm font-semibold disabled:cursor-auto"
												>
													{deletingRecordId === record.id && (
														<LoadingIndicator size={12} color={dangerColor} />
													)}
													Delete
												</button>
											</div>
										),
									],
						),
						[
							<Dropdown
								items={dnsRecordTypes.map(x => ({ id: x, title: x }))}
								selectedItemId={newType}
								onChange={value => setNewType(dnsRecordTypes.find(x => x === value) ?? "A")}
							/>,
							<InputField compact placeholder="@" value={newHost} onChange={setNewHost} />,
							<InputField compact placeholder="192.168.1.1" value={newValue} onChange={setNewValue} />,
							<button
								type="button"
								onClick={handleAddRecord}
								disabled={isSubmittingNew}
								className="text-primary flex cursor-pointer items-center gap-2 self-end text-sm font-semibold disabled:cursor-auto"
							>
								{isSubmittingNew && <LoadingIndicator size={12} />}
								Add record
							</button>,
						],
					]}
				/>
			)}
		</Layout>
	)
})

export default ManageDnsPage
